import { Fragment } from "react";
import {
  Badge,
  Box,
  Heading,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import { LineTypeBadge, RoleBadge, StandsBadge } from "./LineBadges";
import { ReasonText } from "./ReasonFields";
import { usePartyNames } from "../../hooks/usePartyNames";
import { formatDate, formatShareRange, readOffer } from "./placingUtils";

const time = (v) => new Date(v?.$date ?? v ?? 0).getTime();
const newestFirst = (offers) => [...(offers || [])].sort((a, b) => time(b.stampedDate) - time(a.stampedDate));

/** Groups line-request rows by carrier, in the order they came. */
const byCarrier = (rows) =>
  rows.reduce((list, row) => {
    const group = list.find((g) => g.partyId === row.partyId);
    if (group) group.rows.push(row);
    else list.push({ partyId: row.partyId, rows: [row] });
    return list;
  }, []);

/** Each carrier's latest offer, across its line requests, summed. */
const writtenSoFar = (groups) =>
  Math.round(
    groups.reduce((total, g) => {
      const latest = newestFirst(g.rows.flatMap((r) => r.offers || []))[0];
      return total + (Number(readOffer(latest).writtenShare) || 0);
    }, 0) * 100
  ) / 100;

const OfferRow = ({ offer }) => {
  const line = readOffer(offer);
  return (
    <Tr opacity={offer.expired || offer.status === "ntu" ? 0.6 : 1}>
      <Td><PlacingStatusBadge status={offer.status} /></Td>
      <Td fontWeight="semibold">
        {line.writtenShare != null ? `${line.writtenShare}%` : "—"}
        {line.signedShare != null && <Text fontSize="xs" color="green.700">signed {line.signedShare}%</Text>}
      </Td>
      <Td>
        <HStack spacing={1}>
          <LineTypeBadge lineType={line.lineType} />
          {line.lineToStand && <StandsBadge />}
        </HStack>
        {line.conditions && <Text fontSize="xs" color="orange.700" whiteSpace="pre-wrap" mt={1}>{line.conditions}</Text>}
        {offer.conditionsAccepted && (
          <Badge colorScheme="green" variant="outline" mt={1}>
            Conditions accepted{offer.conditionsAcceptedDate ? ` ${formatDate(offer.conditionsAcceptedDate).split(",")[0]}` : ""}
          </Badge>
        )}
      </Td>
      <Td>{offer.reference || "—"}</Td>
      <Td>{offer.stampReference || "—"}</Td>
      <Td>
        {formatDate(offer.validUntilDate) || "—"}
        {offer.expired && <Badge ml={2} colorScheme="red">EXPIRED</Badge>}
      </Td>
      <Td maxW="320px" whiteSpace="pre-wrap" fontSize="xs">{offer.notes || ""}</Td>
      <Td>
        {formatDate(offer.stampedDate)}
        {offer.status === "ntu" && <ReasonText color="orange.700" prefix="NTU · " code={offer.ntuReasonCode} notes={offer.ntuNotes} />}
      </Td>
    </Tr>
  );
};

const RoleGroup = ({ title, rows, names }) => {
  const groups = byCarrier(rows);
  if (groups.length === 0) return null;
  return (
    <Box>
      <HStack justify="space-between" mb={2}>
        <Heading size="xs" textTransform="uppercase" color="gray.600">{title}</Heading>
        <Text fontSize="sm">
          Written so far: <Text as="span" fontWeight="semibold" fontFamily="mono">{writtenSoFar(groups)}%</Text>
          <Text as="span" fontSize="xs" color="gray.500"> · indication only (latest offer per carrier); capacity fill and signing down come later</Text>
        </Text>
      </HStack>
      <Table size="sm">
        <Thead>
          <Tr><Th>Line request</Th><Th>Status</Th><Th>Requested</Th><Th>Response by</Th><Th>Notes</Th></Tr>
        </Thead>
        <Tbody>
          {groups.map((group) => (
            <Fragment key={group.partyId}>
              <Tr bg="gray.100">
                <Td colSpan={5} py={2}>
                  <HStack>
                    <Text fontWeight="bold">{names[group.partyId] || "Loading…"}</Text>
                    <RoleBadge role={group.rows[0].role} />
                    <Text fontSize="xs" color="gray.500">{group.rows.length} line request{group.rows.length > 1 ? "s" : ""}</Text>
                  </HStack>
                </Td>
              </Tr>
              {group.rows.map((row, index) => {
                const offers = newestFirst(row.offers);
                return (
                  <Fragment key={row.lineRequestId}>
                    <Tr>
                      <Td>
                        <Text fontSize="sm">
                          Request {index + 1}
                          {row.sentDate && <Text as="span" color="gray.500"> · sent {formatDate(row.sentDate)}</Text>}
                        </Text>
                        {row.status === "declined" && (
                          <ReasonText
                            color="red.600"
                            prefix={`Declined ${formatDate(row.declinedDate)} · `}
                            code={row.reasonCode ?? row.receiver?.reasonCode}
                            notes={row.declineReason}
                          />
                        )}
                        {row.status === "ntu" && <ReasonText color="orange.700" prefix="NTU · " code={row.ntuReasonCode} notes={row.ntuNotes} />}
                      </Td>
                      <Td><PlacingStatusBadge status={row.status} /></Td>
                      <Td>
                        {row.requestedShare != null ? `${row.requestedShare}%` : "—"}
                        {formatShareRange(row.minimumShare, row.maximumShare) && (
                          <Text fontSize="xs" color="gray.500">{formatShareRange(row.minimumShare, row.maximumShare)}</Text>
                        )}
                      </Td>
                      <Td>
                        {formatDate(row.responseRequiredByDate) || "—"}
                        {row.overdue && <Badge ml={2} colorScheme="red">OVERDUE</Badge>}
                      </Td>
                      <Td maxW="320px" whiteSpace="pre-wrap" fontSize="xs">{row.notes || ""}</Td>
                    </Tr>
                    {offers.length > 0 && (
                      <Tr>
                        <Td colSpan={5} pt={0} pl={8}>
                          <Table size="sm" bg="gray.50">
                            <Thead>
                              <Tr><Th>Status</Th><Th>Written</Th><Th>Line</Th><Th>Reference</Th><Th>Stamp</Th><Th>Valid until</Th><Th>Line detail</Th><Th>Stamped</Th></Tr>
                            </Thead>
                            <Tbody>
                              {offers.map((offer) => <OfferRow key={offer.offerId || offer._id} offer={offer} />)}
                            </Tbody>
                          </Table>
                        </Td>
                      </Tr>
                    )}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

/** Broker: the placement's lines (GET /placements/{id}/lines): leads first, then followers. */
const LinesView = ({ lines }) => {
  const rows = lines?.rows || [];
  const names = usePartyNames(rows.map((r) => r.partyId));
  if (!lines) return null;

  return (
    <VStack align="stretch" spacing={5}>
      <StatGroup>
        <Stat><StatLabel>Line requests</StatLabel><StatNumber>{lines.lineRequests ?? rows.length}</StatNumber></Stat>
        <Stat><StatLabel>Leads</StatLabel><StatNumber>{lines.leads ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Followers</StatLabel><StatNumber>{lines.followers ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Offered</StatLabel><StatNumber color="purple.600">{lines.offered ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Declined</StatLabel><StatNumber color="red.600">{lines.declined ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Awaiting</StatLabel><StatNumber color="blue.600">{lines.awaiting ?? 0}</StatNumber></Stat>
      </StatGroup>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No lines requested yet.</Text>
      ) : (
        <>
          <RoleGroup title="Lead" rows={rows.filter((r) => r.role === "lead")} names={names} />
          <RoleGroup title="Followers" rows={rows.filter((r) => r.role !== "lead")} names={names} />
        </>
      )}
    </VStack>
  );
};

export default LinesView;
